"use client"

import type React from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import type { RootState } from "../store/store"
import LoadingSpinner from "./common/LoadingSpinner"
import { useEffect, useState } from "react"
import { auth } from "../firebase/config"
import { isAdminUser } from "../services/auth"
import toast from "react-hot-toast"

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  redirectTo?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  redirectTo = "/login",
}) => {
  const { user, loading } = useSelector((state: RootState) => state.auth)
  const location = useLocation()
  const navigate = useNavigate()
  const [adminCheck, setAdminCheck] = useState({ loading: false, isAdmin: false, error: null as string | null })

  // Debug logging
  console.log("🔍 ProtectedRoute Debug:", {
    pathname: location.pathname,
    requireAuth,
    requireAdmin,
    redirectTo,
    user: user
      ? {
          email: user.email,
          role: user.role,
          uid: user.uid,
        }
      : null,
    loading,
    authCurrentUser: auth.currentUser
      ? {
          email: auth.currentUser.email,
          emailVerified: auth.currentUser.emailVerified,
          uid: auth.currentUser.uid,
        }
      : null,
    adminCheck,
  })

  useEffect(() => {
    const checkAdmin = async () => {
      if (requireAdmin && user) {
        console.log("🔍 Checking admin status for user:", user.email)
        setAdminCheck({ loading: true, isAdmin: false, error: null })
        try {
          // Check admin status from user role or email
          const isAdmin = user.role === "admin" || isAdminUser(user.email || "")

          console.log("🔍 Admin check results:", {
            userRole: user.role,
            isAdminByRole: user.role === "admin",
            isAdminByEmail: isAdminUser(user.email || ""),
            finalAdminStatus: isAdmin,
          })

          setAdminCheck({ loading: false, isAdmin, error: null })
        } catch (error) {
          console.error("🔍 Admin check error:", error)
          setAdminCheck({ loading: false, isAdmin: false, error: "Failed to verify admin status." })
        }
      } else if (!requireAdmin) {
        // If admin check is not required, set admin to true to avoid blocking
        setAdminCheck({ loading: false, isAdmin: true, error: null })
      } else {
        setAdminCheck({ loading: false, isAdmin: false, error: null })
      }
    }
    checkAdmin()
  }, [requireAdmin, user])

  useEffect(() => {
    // Only restrict unverified users if they're not admins
    if (user && auth.currentUser) {
      const isAdmin = user.role === "admin" || isAdminUser(user.email || "")
      const isEmailVerified = auth.currentUser.emailVerified || isAdmin // Admins bypass email verification

      console.log("🔍 Email verification check:", {
        userEmail: user?.email,
        isEmailVerified,
        isAdmin,
        currentPath: location.pathname,
        willRedirect: !isEmailVerified && location.pathname !== "/",
      })

      if (!isEmailVerified && location.pathname !== "/") {
        console.log("🔍 Redirecting unverified user to homepage")
        toast.error("Please verify your email to access this page.")
        navigate("/", { replace: true })
      }
    }
  }, [user, location, navigate])

  if (loading || adminCheck.loading) {
    console.log("🔍 Showing loading spinner:", { loading, adminCheckLoading: adminCheck.loading })
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (requireAuth && !user) {
    console.log("🔍 No user found, redirecting to login")
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Final access check - allow admins to bypass email verification
  if (user && auth.currentUser) {
    const isAdmin = user.role === "admin" || isAdminUser(user.email || "")
    const isEmailVerified = auth.currentUser.emailVerified || isAdmin

    console.log("🔍 Final access check:", {
      userEmail: user?.email,
      isEmailVerified,
      isAdmin,
      currentPath: location.pathname,
      isHomepage: location.pathname === "/",
      willBlock: !isEmailVerified && location.pathname === "/",
    })

    if (!isEmailVerified && location.pathname === "/") {
      console.log("🔍 Blocking unverified user on homepage")
      return (
        <div className="min-h-screen flex items-center justify-center text-red-600 font-bold">
          Please verify your email to access the rest of the site.
        </div>
      )
    }
  }

  if (requireAdmin) {
    // Quick check for admin users to avoid loading state
    const quickAdminCheck = user && (user.role === "admin" || isAdminUser(user.email || ""))
    
    console.log("🔍 Admin route check:", {
      adminCheckError: adminCheck.error,
      adminCheckIsAdmin: adminCheck.isAdmin,
      quickAdminCheck,
      willRedirect: !adminCheck.isAdmin && !quickAdminCheck,
    })

    if (adminCheck.error) {
      console.log("🔍 Admin check error, showing error message")
      return (
        <div className="min-h-screen flex items-center justify-center text-red-600 font-bold">{adminCheck.error}</div>
      )
    }
    
    // Use quick check if adminCheck is still loading or false
    const isAdmin = adminCheck.isAdmin || quickAdminCheck
    if (!isAdmin) {
      console.log("🔍 Not admin, redirecting to homepage")
      return <Navigate to="/" replace />
    }
  }

  console.log("🔍 Access granted, rendering children")
  return <>{children}</>
}

export default ProtectedRoute
